import { createContext, useState, useEffect, useCallback, useRef } from 'react';
import authService from '../services/authService';
import { needsReacceptance } from '../constants/legal';
import { purgeApiCache } from '../utils/swCache';
import { purgeOfflineDb } from '../services/offlineDb';
import { gardenStorage } from '../services/gardenStorage';

export const AuthContext = createContext(null);

/**
 * Client-only hint that the user *might* have an active backend session.
 * Set after a successful verifyToken, cleared on logout or on a 401 from /auth/me.
 * Anonymous first-time visitors don't have it → getMe() is skipped → no 401 in
 * the console (the browser auto-logs failed responses; only "no request" avoids
 * the log). The hint is NOT a security claim — the HttpOnly cookie still gates
 * the backend; the hint only controls whether we bother asking.
 *
 * See #457.
 */
const SESSION_HINT_KEY = 'mgr_session_hint';

const hasSessionHint = () => {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage?.getItem(SESSION_HINT_KEY) === '1';
  } catch {
    return false;
  }
};

const setSessionHint = () => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage?.setItem(SESSION_HINT_KEY, '1');
  } catch {
    // localStorage unavailable (private mode quota etc.) — non-fatal
  }
};

const clearSessionHint = () => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage?.removeItem(SESSION_HINT_KEY);
  } catch {
    // non-fatal
  }
};

/**
 * Drop locally cached garden traces (SEC-20). On a shared device the next user
 * must not see the previous user's garden data that lives in localStorage.
 * Called on logout, logout-all, and account deletion.
 *
 * #570: also clears `gartenplaner_canvas` and `gartenplaner_garden_settings` —
 * these hold the last active garden's canvas + settings and were previously left
 * behind on logout (pre-existing SEC-20 gap). The offline read path (#570 Stufe
 * 1) treats these as an offline source, so leaving them would leak the previous
 * user's layout to the next one.
 */
const clearLocalGardenData = () => {
  if (typeof window === 'undefined') return;
  try {
    gardenStorage.clear(); // gartenplaner_my_gardens
    window.localStorage?.removeItem('my_garden_ids');
    window.localStorage?.removeItem('gartenplaner_canvas');
    window.localStorage?.removeItem('gartenplaner_garden_settings');
    window.localStorage?.removeItem('gartenplaner_active_garden_id');
    // Favorites are user-personal preferences (device-wide, transcend gardens),
    // but must not survive a logout / user switch — otherwise the next user on a
    // shared device inherits them.
    window.localStorage?.removeItem('gartenplaner_favorites');
  } catch {
    // localStorage unavailable — non-fatal
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  // Only "loading" when there is a session hint to verify — otherwise the initial
  // /auth/me request is skipped, so derive the start value instead of flipping it
  // synchronously inside the effect.
  const [loading, setLoading] = useState(() => hasSessionHint());
  const [sessionExpired, setSessionExpired] = useState(false);

  // Set to true after the initial session check completes with an authenticated user.
  // Prevents the SessionExpiredModal from firing for anonymous 401s on startup.
  const hadSession = useRef(false);

  useEffect(() => {
    // Skip /auth/me when no session hint exists. Browsers log every failed
    // network response (incl. 401) to the DevTools/Playwright console;
    // suppressing in JS only handles the JS-error path. The only way to keep
    // anonymous first-load console clean is not to make the request. See #457.
    if (!hasSessionHint()) {
      return;
    }
    authService
      .getMe()
      .then((u) => {
        setUser(u);
        if (u) hadSession.current = true;
      })
      .catch(() => {
        setUser(null);
        // Cookie is no longer valid — drop the hint so the next reload stays quiet.
        clearSessionHint();
      })
      .finally(() => setLoading(false));
  }, []);

  // Listen for 401s from the main app API layer (api.js).
  // Only trigger the modal when the user had an active session.
  useEffect(() => {
    const handle401 = () => {
      if (hadSession.current) {
        setSessionExpired(true);
      }
    };
    window.addEventListener('auth:401', handle401);
    return () => window.removeEventListener('auth:401', handle401);
  }, []);

  const requestMagicLink = useCallback(async (email, agbAccepted = false) => {
    await authService.requestMagicLink(email, agbAccepted);
    return true;
  }, []);

  const acceptAgb = useCallback(async () => {
    const updated = await authService.acceptAgb();
    setUser(updated);
    return updated;
  }, []);

  const verifyToken = useCallback(async (token) => {
    const result = await authService.verifyToken(token);
    if (result.success) {
      setUser(result.user);
      hadSession.current = true;
      setSessionHint();
      const gardenIds = JSON.parse(localStorage.getItem('my_garden_ids') || '[]');
      if (gardenIds.length > 0) {
        try {
          await authService.claimGardens(gardenIds);
          localStorage.removeItem('my_garden_ids');
        } catch (err) {
          // #446 C-3: surface the failure so it can be diagnosed. We keep
          // my_garden_ids in localStorage on error (the removeItem above only
          // runs after a successful claim), so the next successful verify
          // will retry the claim.
          console.warn('claimGardens failed; will retry on next login:', err);
        }
      }
    }
    return result;
  }, []);

  const logout = useCallback(async () => {
    await authService.logout();
    setUser(null);
    hadSession.current = false;
    clearSessionHint();
    clearLocalGardenData(); // SEC-20
    // SEC-19: drop any cached personal API responses so a follow-up user on a
    // shared device can't read them via the Cache Storage fallback.
    await purgeApiCache();
    await purgeOfflineDb(); // #570: drop offline-cached gardens (SEC-19/20)
  }, []);

  // Revoke every session for this account (SEC-07, #525), then drop the local
  // session state exactly like logout. The current cookie is invalidated
  // server-side too, so this device is logged out as well.
  const logoutAll = useCallback(async () => {
    await authService.logoutAll();
    setUser(null);
    hadSession.current = false;
    clearSessionHint();
    clearLocalGardenData(); // SEC-20
    await purgeApiCache(); // SEC-19
    await purgeOfflineDb(); // #570
  }, []);

  // Permanently delete the account (Art. 17 DSGVO, #529). The backend removes
  // all data and clears the session cookie; we then drop every local trace
  // (session hint + the anonymous-garden claim list) so a fresh start is clean.
  const deleteAccount = useCallback(async () => {
    await authService.deleteAccount();
    setUser(null);
    hadSession.current = false;
    clearSessionHint();
    clearLocalGardenData(); // SEC-20 (incl. my_garden_ids)
    await purgeApiCache(); // SEC-19
    await purgeOfflineDb(); // #570
  }, []);

  // Download all personal data as a JSON file (Art. 15/20 DSGVO, #529).
  const exportData = useCallback(async () => {
    await authService.exportData();
  }, []);

  const clearSessionExpired = useCallback(() => {
    setSessionExpired(false);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isAuthenticated: !!user,
        needsAgbReacceptance: !!user && needsReacceptance(user.agb_version),
        sessionExpired,
        clearSessionExpired,
        requestMagicLink,
        verifyToken,
        logout,
        logoutAll,
        acceptAgb,
        deleteAccount,
        exportData,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
